import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, onSnapshot, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import {
  Box, Typography, Button, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup,
  Rating, TextField, useMediaQuery, Container, Paper, Divider, Card, CardMedia, CardContent,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { styled } from '@mui/system';

const StyledPaper = styled(Paper)(({ theme, darkMode }) => ({
  padding: theme.spacing(4),
  borderRadius: '12px',
  boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
  background: darkMode ? 'linear-gradient(145deg, #2A2A40, #1e1e2f)' : 'linear-gradient(145deg, #ffffff, #f9fafb)',
  maxWidth: '600px',
  margin: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)(({ theme, darkMode }) => ({
  borderRadius: '8px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: darkMode ? '#4b5563' : '#735DA5',
  color: '#ffffff',
  '&:hover': {
    boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.7)' : '0 4px 12px rgba(0,0,0,0.15)',
    backgroundColor: darkMode ? '#6b7280' : '#a583f2',
  },
}));

const SuccessContainer = styled(Box)(({ theme, darkMode }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  '@keyframes scaleIn': {
    '0%': { transform: 'scale(0.5)', opacity: 0 },
    '100%': { transform: 'scale(1)', opacity: 1 },
  },
  '& .success-icon': {
    animation: 'scaleIn 0.5s ease-in-out',
  },
  backgroundColor: darkMode ? '#2d2d44' : '#f9fafb',
  color: darkMode ? '#d1d5db' : '#1f2937',
}));

const FeedbackSuccessContainer = styled(Box)(({ theme, darkMode }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  '@keyframes fadeIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  '& .feedback-icon': {
    animation: 'fadeIn 0.5s ease-in-out',
  },
  backgroundColor: darkMode ? '#2d2d44' : '#f9fafb',
  color: darkMode ? '#d1d5db' : '#1f2937',
}));

const PendingContainer = styled(Box)(({ theme, darkMode }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  backgroundColor: darkMode ? '#2d2d44' : '#f9fafb',
  color: darkMode ? '#d1d5db' : '#1f2937',
}));

const ThankYouHeader = styled(Box)(({ theme, darkMode }) => ({
  background: darkMode ? 'linear-gradient(90deg, #1e1e2f, #3a3a5c)' : 'linear-gradient(90deg, #1976d2, #42a5f5)',
  color: '#fff',
  padding: theme.spacing(2),
  borderRadius: '12px 12px 0 0',
  textAlign: 'center',
  margin: `-${theme.spacing(4)} -${theme.spacing(4)} ${theme.spacing(2)} -${theme.spacing(4)}`,
  [theme.breakpoints.down('sm')]: {
    margin: `-${theme.spacing(2)} -${theme.spacing(2)} ${theme.spacing(2)} -${theme.spacing(2)}`,
  },
}));

const StyledReviewCard = styled(Card)(({ theme, darkMode }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  boxShadow: darkMode ? '0 2px 10px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.05)',
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
  },
  backgroundColor: darkMode ? '#3a3a5c' : '#ffffff',
  color: darkMode ? '#d1d5db' : '#1f2937',
}));

const MakePayment = ({ darkMode, unitCode }) => {
  const location = useLocation();
  const state = location?.state;
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [hasPaymentRecorded, setHasPaymentRecorded] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const [overallRating, setOverallRating] = useState(0);
  const [comment, setComment] = useState('');
  const [itemRatings, setItemRatings] = useState({});
  const [menuItems, setMenuItems] = useState({});
  const [totalAmount, setTotalAmount] = useState(state?.totalAmount || 0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const orderId = state?.orderId || localStorage.getItem('currentOrderId') || null;
  const tableId = state?.tableId || (location?.search ? new URLSearchParams(location.search).get('id') : null) || null;
  const companyCode = state?.companyCode || (location?.search ? new URLSearchParams(location.search).get('code') : null) || null;
  const stateUnitCode = state?.unitCode || (location?.search ? new URLSearchParams(location.search).get('unit') : null) || null;
  const effectiveUnitCode = unitCode || stateUnitCode;

  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    console.log('MakePayment: Initial state:', { state, location: location?.pathname, query: location?.search, orderId, tableId, companyCode, unitCode: effectiveUnitCode, totalAmount });

    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        console.log('MakePayment: Razorpay SDK loaded successfully');
        setIsScriptLoaded(true);
      };
      document.body.appendChild(script);
    };

    const fetchCompanyDetails = async () => {
      if (!companyCode) {
        setCompanyName('Unknown Company');
        console.log('MakePayment: No companyCode provided');
        return;
      }
      try {
        const companyDocRef = doc(db, 'companies', companyCode);
        const companyDoc = await getDoc(companyDocRef);
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          setCompanyName(companyData.name || 'Unknown Company');
          console.log('MakePayment: Company details fetched:', { name: companyData.name, companyCode });
        } else {
          setCompanyName('Unknown Company');
          console.log('MakePayment: No company found for companyCode:', companyCode);
        }
      } catch (err) {
        console.error('MakePayment: Error fetching company details:', err);
        setCompanyName('Unknown Company');
      }
    };

    const fetchMenuItems = async () => {
      if (!companyCode || !effectiveUnitCode || !order || !order.items) return;
      console.log('MakePayment: Fetching menu items for companyCode:', companyCode, 'unitCode:', effectiveUnitCode);
      try {
        const menuQuery = query(
          collection(db, 'companymenu'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', effectiveUnitCode)
        );
        const menuSnapshot = await getDocs(menuQuery);
        console.log('MakePayment: Query snapshot size:', menuSnapshot.size);
        const itemMap = {};
        menuSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const itemId = doc.id;
          itemMap[itemId] = { name: data.name, imageUrl: data.imageUrl || '' };
          console.log('MakePayment: Found menu item:', { itemId, name: data.name, imageUrl: data.imageUrl });
        });
        console.log('MakePayment: Menu items fetched:', Object.keys(itemMap));
        if (Object.keys(itemMap).length === 0) {
          console.warn('MakePayment: No menu items found for companyCode:', companyCode, 'unitCode:', effectiveUnitCode);
        }
        const items = {};
        order.items.forEach(item => {
          if (itemMap[item.id]) {
            items[item.id] = itemMap[item.id].imageUrl || 'https://placehold.co/120x120';
          } else {
            console.warn('MakePayment: No image found for item:', item.id);
            items[item.id] = 'https://placehold.co/120x120';
          }
        });
        setMenuItems(items);
        console.log('MakePayment: Menu items with images:', items);
      } catch (err) {
        console.error('MakePayment: Error fetching menu items:', err);
        const fallbackItems = {};
        order.items.forEach(item => {
          fallbackItems[item.id] = 'https://placehold.co/120x120';
        });
        setMenuItems(fallbackItems);
      }
    };

    loadRazorpayScript();
    fetchCompanyDetails();

    const fetchOrder = async () => {
      if (!orderId || !companyCode || !tableId || !effectiveUnitCode) {
        setLoading(false);
        console.log('MakePayment: Missing parameters:', { orderId, companyCode, tableId, unitCode: effectiveUnitCode });
        return;
      }
      try {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', effectiveUnitCode),
          where('tableId', '==', tableId)
        );
        const querySnapshot = await getDocs(ordersQuery);
        let matchingOrder = null;
        querySnapshot.forEach((doc) => {
          if (doc.id === orderId) {
            matchingOrder = { id: doc.id, ...doc.data() };
          }
        });
        if (matchingOrder) {
          setOrder(matchingOrder);
          setTotalAmount(matchingOrder.totalAmount || 0);
          if (matchingOrder.paymentStatus === 'completed' && matchingOrder.paid) {
            setPaymentSuccess(true);
            setHasPaymentRecorded(true);
            setPaymentDetails({
              paymentId: matchingOrder.paymentId || (matchingOrder.paymentMethod === 'cash' ? `cash_${orderId}_${Date.now()}` : null),
              method: matchingOrder.paymentMethod === 'cash' ? 'Cash' : 'Online (UPI/Card)',
            });
          } else if (matchingOrder.paymentStatus === 'pending_confirmation' && matchingOrder.paymentMethod === 'cash') {
            setPaymentPending(true);
          }
        } else {
          setOrder(null);
          setTotalAmount(0);
          console.log('MakePayment: No matching order found for orderId:', orderId, 'companyCode:', companyCode, 'unitCode:', effectiveUnitCode, 'tableId:', tableId);
        }
      } catch (err) {
        console.error('MakePayment: Error fetching order:', err);
        setOrder(null);
        setTotalAmount(0);
      }
      setLoading(false);
    };

    fetchOrder();

    if (orderId) {
      const orderRef = doc(db, 'orders', orderId);
      const unsubscribe = onSnapshot(orderRef, async (doc) => {
        if (doc.exists()) {
          const orderData = doc.data();
          setOrder(orderData);
          setTotalAmount(orderData.totalAmount || 0);
          if (orderData.paymentStatus === 'completed' && orderData.paid && !hasPaymentRecorded) {
            const generatedPaymentId = orderData.paymentMethod === 'cash' ? `cash_${orderId}_${Date.now()}` : orderData.paymentId;
            setPaymentSuccess(true);
            setPaymentPending(false);
            setPaymentDetails({
              paymentId: generatedPaymentId,
              method: orderData.paymentMethod === 'cash' ? 'Cash' : 'Online (UPI/Card)',
            });
            try {
              const paymentId = `payment_${orderId}_${Date.now()}`;
              const paymentData = {
                tableId: tableId || 'N/A',
                orderId: orderId,
                companyCode: companyCode || 'N/A',
                unitCode: effectiveUnitCode || 'N/A',
                date: serverTimestamp(),
                amount: totalAmount,
                paymentMethod: orderData.paymentMethod,
                paymentId: generatedPaymentId,
              };
              await setDoc(doc(db, 'payments', paymentId), paymentData);
              console.log('MakePayment: Payment recorded in payments collection:', paymentId, paymentData);
              setHasPaymentRecorded(true);
              await fetchMenuItems();
            } catch (err) {
              console.error('MakePayment: Error creating payment record:', err);
            }
          } else if (orderData.paymentStatus === 'pending_confirmation' && orderData.paymentMethod === 'cash') {
            setPaymentPending(true);
            setPaymentSuccess(false);
          }
        }
      }, (err) => {
        console.error('MakePayment: Error in order listener:', err);
      });
      return () => unsubscribe();
    }
  }, [orderId, tableId, companyCode, effectiveUnitCode, hasPaymentRecorded, order, totalAmount]);

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  const payNow = async () => {
    if (!isScriptLoaded) {
      console.log('MakePayment: Razorpay script not loaded');
      return;
    }
    if (!order || !totalAmount) {
      console.log('MakePayment: Missing order or totalAmount');
      return;
    }
    const options = {
      key: 'rzp_test_9Mbg2bbubhgN1z',
      amount: totalAmount * 100,
      currency: 'INR',
      name: companyName || 'Platterly',
      description: `Payment for Table ${tableId || 'N/A'} (Order ID: ${orderId || 'N/A'}, Unit: ${effectiveUnitCode || 'N/A'})`,
      handler: async function (response) {
        try {
          const docRef = doc(db, 'orders', orderId);
          await updateDoc(docRef, {
            paid: true,
            paymentId: response.razorpay_payment_id,
            paymentMethod: 'online',
            paymentStatus: 'completed',
            updatedAt: new Date(),
          });
          console.log('MakePayment: Payment successful, updated Firestore:', response);
          const paymentId = `payment_${orderId}_${Date.now()}`;
          const paymentData = {
            tableId: tableId || 'N/A',
            orderId: orderId,
            companyCode: companyCode || 'N/A',
            unitCode: effectiveUnitCode || 'N/A',
            date: serverTimestamp(),
            amount: totalAmount,
            paymentMethod: 'online',
            paymentId: response.razorpay_payment_id,
          };
          await setDoc(doc(db, 'payments', paymentId), paymentData);
          console.log('MakePayment: Payment recorded in payments collection:', paymentId, paymentData);
          setPaymentSuccess(true);
          setHasPaymentRecorded(true);
          setPaymentDetails({
            paymentId: response.razorpay_payment_id,
            method: 'Online (UPI/Card)',
          });
        } catch (err) {
          console.error('MakePayment: Error updating payment status:', err);
        }
      },
      prefill: {
        name: 'Customer',
        email: 'customer@example.com',
        contact: '',
      },
      theme: {
        color: darkMode ? '#c4a1ff' : '#a583f2',
      },
      methods: {
        upi: true,
        card: true,
        netbanking: false,
        wallet: false,
      },
    };
    console.log('MakePayment: Razorpay Options:', options);
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.error('MakePayment: Payment failed:', response);
    });
    rzp.open();
  };

  const handleCashPayment = async () => {
    if (!order || !totalAmount || !companyCode || !effectiveUnitCode) {
      console.log('MakePayment: Missing required fields for cash payment:', { order, totalAmount, companyCode, unitCode: effectiveUnitCode });
      return;
    }
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'servers'), where('companyCode', '==', companyCode));
      const querySnapshot = await getDocs(q);
      const serverIds = [];
      querySnapshot.forEach((doc) => {
        const docId = doc.id;
        if (docId.startsWith(`${companyCode}#`)) {
          serverIds.push(docId);
        }
      });
      console.log('MakePayment: Found servers for companyCode:', companyCode, serverIds);
      if (serverIds.length === 0) {
        console.log('MakePayment: No servers found for companyCode:', companyCode);
      }
      const notificationPromises = serverIds.map(async (serverId) => {
        const notificationId = `server_${serverId}_${orderId}_${Date.now()}`;
        const notificationData = {
          type: 'cash_payment_request',
          orderId: orderId,
          tableId: tableId || 'N/A',
          companyCode: companyCode || 'N/A',
          unitCode: effectiveUnitCode || 'N/A',
          totalAmount: totalAmount,
          message: `Cash payment of ₹${totalAmount.toFixed(2)} requested for Order ${orderId} at Table ${tableId || 'N/A'} (Unit: ${effectiveUnitCode || 'N/A'})`,
          recipientId: serverId,
          role: 'servers',
          createdAt: serverTimestamp(),
          read: false,
        };
        await setDoc(doc(db, 'notifications', notificationId), notificationData);
        console.log('MakePayment: Cash payment notification sent to server:', serverId, notificationId);
      });
      await Promise.all(notificationPromises);
      const docRef = doc(db, 'orders', orderId);
      await updateDoc(docRef, {
        paymentMethod: 'cash',
        paymentStatus: 'pending_confirmation',
        updatedAt: new Date(),
      });
      console.log('MakePayment: Cash payment request recorded, awaiting server confirmation');
      setPaymentPending(true);
    } catch (err) {
      console.error('MakePayment: Error processing cash payment:', err);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!orderId || !tableId || !companyCode || !effectiveUnitCode) {
      console.log('MakePayment: Missing required fields for feedback:', { orderId, tableId, companyCode, unitCode: effectiveUnitCode });
      return;
    }
    try {
      const reviewId = `review_${orderId}_${Date.now()}`;
      const reviewData = {
        orderId: orderId,
        tableId: tableId,
        companyCode: companyCode,
        unitCode: effectiveUnitCode,
        overallRating: overallRating || null,
        comment: comment || 'No comment provided',
        itemRatings: Object.keys(itemRatings).length > 0 ? itemRatings : null,
        timestamp: serverTimestamp(),
        customerId: `${companyCode}#${tableId}`,
      };
      await setDoc(doc(db, 'reviews', reviewId), reviewData);
      console.log('MakePayment: Review submitted:', reviewId, reviewData);

      const batch = writeBatch(db);
      const ratedItemIds = Object.keys(itemRatings);
      if (ratedItemIds.length > 0) {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', effectiveUnitCode)
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const allRatings = reviewsSnapshot.docs
          .flatMap(doc => {
            const data = doc.data();
            return data.itemRatings ? Object.entries(data.itemRatings).map(([itemId, rating]) => ({ itemId, rating })) : [];
          })
          .filter(rating => rating.rating !== undefined);

        const ratingsByItem = allRatings.reduce((acc, { itemId, rating }) => {
          if (!acc[itemId]) acc[itemId] = [];
          acc[itemId].push(rating);
          return acc;
        }, {});

        ratedItemIds.forEach(itemId => {
          const itemRatings = ratingsByItem[itemId] || [];
          const averageRating = itemRatings.length > 0
            ? Number((itemRatings.reduce((sum, rating) => sum + rating, 0) / itemRatings.length).toFixed(1))
            : null;
          const itemDocRef = doc(db, 'companymenu', itemId);
          batch.update(itemDocRef, { averageRating }, { merge: true });
          console.log(`MakePayment: Queued update for item ${itemId} with averageRating: ${averageRating}`);
        });

        await batch.commit();
        console.log('MakePayment: Updated average ratings for rated items in companymenu');
      }

      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('MakePayment: Error submitting review or updating ratings:', err);
    }
  };

  const handleSkipFeedback = () => {
    console.log('MakePayment: Skipping feedback, navigating to menu:', { companyCode, unitCode: effectiveUnitCode, tableId });
    navigate(`/customer/menu?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
      state: { tableId, companyCode, unitCode: effectiveUnitCode }
    });
  };

  const handleItemRatingChange = (itemId, newValue) => {
    setItemRatings((prev) => ({
      ...prev,
      [itemId]: newValue || null,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? 'xs' : 'lg'} sx={{ py: 3, px: isMobile ? 1 : 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, backgroundColor: darkMode ? '#1e1e2f' : '#f9fafb' }}>
          <CircularProgress sx={{ color: darkMode ? '#93c5fd' : '#93c5fd' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? 'xs' : 'lg'} sx={{ py: 3, px: isMobile ? 1 : 2, minHeight: '100vh' }}>
      <StyledPaper elevation={3} darkMode={darkMode}>
        {!orderId || !tableId || !companyCode || !effectiveUnitCode ? (
          <Box sx={{ textAlign: 'center', py: 3, color: darkMode ? '#d1d5db' : '#1f2937' }}>
            <Typography variant="h6" color="text.secondary">
              Please place the order first, then make payment.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/customer/menu?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
                state: { tableId, companyCode, unitCode: effectiveUnitCode }
              })}
              sx={{ mt: 2, backgroundColor: darkMode ? '#4b5563' : '#735DA5', color: '#ffffff', '&:hover': { backgroundColor: darkMode ? '#6b7280' : '#a583f2' } }}
            >
              Back to Menu
            </Button>
          </Box>
        ) : paymentSuccess ? (
          paymentDetails ? (
            feedbackSubmitted ? (
              <FeedbackSuccessContainer darkMode={darkMode}>
                <ThankYouHeader darkMode={darkMode}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
                    Thank You for Your Feedback!
                  </Typography>
                </ThankYouHeader>
                <CheckCircleOutline
                  className="feedback-icon"
                  sx={{ fontSize: isMobile ? 50 : 60, color: darkMode ? '#a3e4d7' : '#2e7d32' }}
                  aria-label="Feedback submitted"
                />
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600 }}>
                  Thank you for giving feedback! Visit us again soon!
                </Typography>
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/customer/menu?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
                    state: { tableId, companyCode, unitCode: effectiveUnitCode }
                  })}
                  sx={{ mt: 2 }}
                >
                  Back to Menu
                </StyledButton>
              </FeedbackSuccessContainer>
            ) : (
              <SuccessContainer darkMode={darkMode}>
                <ThankYouHeader darkMode={darkMode}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
                    Thank You for Your Payment!
                  </Typography>
                </ThankYouHeader>
                <CheckCircleOutline
                  className="success-icon"
                  sx={{ fontSize: isMobile ? 50 : 60, color: darkMode ? '#a3e4d7' : '#2e7d32' }}
                  aria-label="Payment successful"
                />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Rate each item (optional, skip if desired):
                  </Typography>
                  {order.items.map((item, index) => (
                    <StyledReviewCard key={`${item.id}-${index}`} darkMode={darkMode}>
                      <CardMedia
                        component="img"
                        sx={{ width: isMobile ? '100%' : 120, height: 120, objectFit: 'cover', borderRadius: '12px 0 0 12px' }}
                        image={menuItems[item.id] || 'https://placehold.co/120x120'}
                        alt={item.name}
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/120x120';
                          console.error(`Image load failed for ${item.id}: ${menuItems[item.id]}`);
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, p: isMobile ? 1 : 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {item.name}
                        </Typography>
                        <Rating
                          value={itemRatings[item.id] || 0}
                          onChange={(event, newValue) => handleItemRatingChange(item.id, newValue)}
                          precision={0.5}
                          size={isMobile ? 'small' : 'medium'}
                          sx={{ mt: 1, color: darkMode ? '#facc15' : '#f59e0b' }}
                        />
                      </CardContent>
                    </StyledReviewCard>
                  ))}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                    Overall Rating (optional):
                  </Typography>
                  <Rating
                    value={overallRating}
                    onChange={(event, newValue) => setOverallRating(newValue)}
                    precision={0.5}
                    size={isMobile ? 'medium' : 'large'}
                    sx={{ mt: 1, color: darkMode ? '#facc15' : '#f59e0b' }}
                  />
                </Box>
                <TextField
                  label="Your Feedback (Optional)"
                  multiline
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    backgroundColor: darkMode ? '#3a3a5c' : '#ffffff',
                    color: darkMode ? '#d1d5db' : '#1f2937',
                    '& .MuiInputBase-input': { color: darkMode ? '#d1d5db' : '#1f2937' },
                    '& .MuiInputLabel-root': { color: darkMode ? '#9ca3af' : '#64748b' }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitFeedback}
                    fullWidth={isMobile}
                  >
                    Submit Feedback
                  </StyledButton>
                  <StyledButton
                    variant="outlined"
                    color="primary"
                    onClick={handleSkipFeedback}
                    fullWidth={isMobile}
                  >
                    Skip
                  </StyledButton>
                </Box>
              </SuccessContainer>
            )
          ) : null
        ) : paymentPending ? (
          <PendingContainer darkMode={darkMode}>
            <CircularProgress sx={{ fontSize: 50, color: darkMode ? '#93c5fd' : '#ed6c02' }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: darkMode ? '#93c5fd' : '#ed6c02' }}>
              Cash Payment Requested
            </Typography>
            <Typography variant="body1" color={darkMode ? '#9ca3af' : 'text.secondary'} sx={{ maxWidth: '80%' }}>
              Your cash payment request has been sent. Please wait for staff to confirm the payment.
            </Typography>
            <Typography variant="body2" color={darkMode ? '#9ca3af' : 'text.secondary'}>
              Order ID: {orderId || 'N/A'}
            </Typography>
            <Typography variant="body2" color={darkMode ? '#9ca3af' : 'text.secondary'}>
              Table: {tableId || 'N/A'}
            </Typography>
            <Typography variant="body2" color={darkMode ? '#9ca3af' : 'text.secondary'}>
              Unit: {effectiveUnitCode || 'N/A'}
            </Typography>
            <Typography variant="body2" color={darkMode ? '#9ca3af' : 'text.secondary'}>
              Total Amount: ₹{totalAmount.toFixed(2)}
            </Typography>
            <StyledButton
              variant="outlined"
              color="primary"
              onClick={() => navigate(`/customer/bill?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
                state: { tableId, orderId, cart: order?.items, totalAmount, companyCode, unitCode: effectiveUnitCode }
              })}
              sx={{ mt: 2 }}
            >
              Back to Bill
            </StyledButton>
          </PendingContainer>
        ) : (
          <>
            <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: darkMode ? '#d1d5db' : '#1f2937' }}>
              Make Payment
            </Typography>
            {!order && (
              <Box sx={{ textAlign: 'center', py: 2, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                <Typography variant="body1" color="text.secondary">
                  No order details available.
                </Typography>
              </Box>
            )}
            {order && (
              <>
                <Box sx={{ mb: 3, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Table: {tableId || 'N/A'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Unit: {effectiveUnitCode || 'N/A'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Order ID: {orderId || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 500 }}>
                    Ordered Items:
                  </Typography>
                  {order.items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                      <Typography variant="body1">
                        {item.name}
                      </Typography>
                      <Typography variant="body1">
                        ₹{item.price.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 2, backgroundColor: darkMode ? '#9ca3af' : '#e5e7eb' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#d1d5db' : '#1f2937' }}>
                      Total Amount:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#a583f2' : '#735DA5' }}>
                      ₹{totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: darkMode ? '#d1d5db' : '#1f2937', mb: 1 }}>
                    Select Payment Method
                  </Typography>
                  <RadioGroup value={paymentMethod} onChange={handlePaymentMethodChange} row={!isMobile}>
                    <FormControlLabel
                      value="online"
                      control={<Radio color="primary" />}
                      label="UPI/Card"
                      sx={{ mr: 3, '& .MuiTypography-root': { color: darkMode ? '#d1d5db' : '#1f2937' } }}
                    />
                    <FormControlLabel
                      value="cash"
                      control={<Radio color="primary" />}
                      label="Cash"
                      sx={{ '& .MuiTypography-root': { color: darkMode ? '#d1d5db' : '#1f2937' } }}
                    />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  {paymentMethod === 'online' ? (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={payNow}
                      disabled={order.paid || !isScriptLoaded}
                      fullWidth={isMobile}
                      sx={{ width: 200 }}
                    >
                      {order.paid ? 'Paid' : 'Pay Now'}
                    </StyledButton>
                  ) : (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={handleCashPayment}
                      disabled={order.paid || order.paymentStatus === 'pending_confirmation'}
                      fullWidth={isMobile}
                    >
                      {order.paymentStatus === 'pending_confirmation' ? 'Cash Payment Requested' : 'Pay Now'}
                    </StyledButton>
                  )}
                  <StyledButton
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(`/customer/bill?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
                      state: { tableId, orderId, cart: order?.items, totalAmount, companyCode, unitCode: effectiveUnitCode }
                    })}
                    fullWidth={isMobile}
                  >
                    Back to Bill
                  </StyledButton>
                </Box>
              </>
            )}
          </>
        )}
      </StyledPaper>
    </Container>
  );
};

export default MakePayment;